import { VIEW_WIDGET_KEY } from '../../view/IViewWidget.js';
import { ViewWidgetHelper } from '../../view/ViewWidgetHelper.js';

// src/core/imcmd/SearchMask.ts
const DEFAULT_AREA = 'view';
/**
 * Алиасы для зон поиска:
 * v → view, i → inherit, g → global, c → closest, p → parent, pi → parent-inherit
 */
const AREA_ALIASES = {
    v: 'view',
    i: 'inherit',
    g: 'global',
    c: 'closest',
    p: 'parent',
    pi: 'parent-inherit',
    view: 'view',
    inherit: 'inherit',
    global: 'global',
    closest: 'closest',
    parent: 'parent',
    'parent-inherit': 'parent-inherit',
};
/**
 * Выделение собственной зоны строки поиска: хвост после последнего ^,
 * сверенный с таблицей AREA_ALIASES.
 *
 * Единственное место, где принимается решение «это зона, а не часть селектора»:
 * им пользуются и разбор строки, и проверка наличия зоны, поэтому разойтись они не могут.
 *
 * @param src Строка поиска без обрамляющих пробелов
 * @returns Зона и остаток строки без хвоста, либо undefined, если зоны нет
 */
function splitOwnArea(src) {
    // ищем последний ^
    const caretIdx = src.lastIndexOf('^');
    if (caretIdx < 0)
        return undefined;
    const tail = src.slice(caretIdx + 1).trim().toLowerCase();
    const resolved = AREA_ALIASES[tail];
    // хвост не совпал с зоной → оставляем ^ в селекторе
    if (!resolved)
        return undefined;
    return { area: resolved, head: src.slice(0, caretIdx).trim() };
}
/**
 * Проверка: несет ли строка поиска собственную зону.
 *
 * Нужна тем, кто собирается дополнить маску зоной со стороны: собственная зона маски конкретнее
 * присланной, поэтому при ее наличии внешнюю приклеивать нельзя — иначе в строке окажется два ^,
 * разбор возьмет внешний, а остаток с ^ уйдет в селектор и сделает его невалидным.
 *
 * @param input Строка поиска
 * @returns true, если маска уже задает зону суффиксом после ^
 */
function hasOwnSearchArea(input) {
    return splitOwnArea(String(input ?? '').trim()) !== undefined;
}
/**
 * Парсер строки поиска → SearchMask
 *
 * Правила:
 * - Берём последний ^ и проверяем хвост после него:
 *   если это один из известных ключей (или алиасов), то
 *   - устанавливаем зону area
 *   - отрезаем хвост ^...
 *   если не совпало — считаем ^ частью обычного CSS-селектора.
 *
 * - Все вхождения @someKey заменяем на [data-key="someKey"]
 *   (или [data-tablekey="..."], если передан tableMode=true).
 *
 * - В itemKey сохраняем первый найденный ключ (без @).
 */
function parseSearchMask(input, tableMode = false) {
    const src = String(input ?? '').trim();
    // --- 1. Определяем зону поиска ---
    const own = splitOwnArea(src);
    const area = own?.area ?? DEFAULT_AREA;
    const head = own?.head ?? src;
    // --- 2. Заменяем @ключи на [data-(table)key="..."] ---
    const attr = tableMode ? 'data-tablekey' : 'data-key';
    const atRe = /@([A-Za-z_][\w\-.:]*)/g; // ищем @ключи
    let firstKey;
    const mask = head.replace(atRe, (_m, key) => {
        if (firstKey === undefined)
            firstKey = key;
        return `[${attr}="${key}"]`;
    }).trim();
    return { mask: mask || '', area, itemKey: firstKey };
}
/** CSS-селектор элемента, являющегося корнем view */
const VIEW_SELECTOR = `[data-widget~="${VIEW_WIDGET_KEY}"]`;
/**
 * Поиск элементов по уже распарсенному SearchMask.
 * @param viewHost Корень текущего view (элемент с data-widget, содержащим "View")
 * @param sm mask + area
 * @param sourceElement Стартовая точка для зоны `closest`
 * @param uiNotMask Селектор-ограничитель для исключения контента вложенных view
 */
function findBySearchMask(viewHost, sm, sourceElement, uiNotMask) {
    let resultSm;
    if (typeof sm === 'string') {
        resultSm = parseSearchMask(sm);
    }
    else {
        resultSm = sm;
    }
    const { mask, area } = resultSm;
    if (!mask)
        return [];
    switch (area) {
        case 'global': {
            const root = viewHost.ownerDocument ?? document;
            return qAll(root, mask);
        }
        case 'closest': {
            const start = (sourceElement ?? viewHost);
            const fromParent = start.parentElement;
            const hit = fromParent ? fromParent.closest(mask) : null;
            return hit ? [hit] : [];
        }
        case 'inherit': {
            // разрешаем заход во вложенные view
            return qAll(viewHost, mask);
        }
        case 'parent-inherit': {
            // В родительском view, разрешая вход во вложенные
            const parentView = findParentView(viewHost);
            return parentView ? qAll(parentView, mask) : [];
        }
        case 'parent': {
            // В родительском view, НЕ заходя во вложенные → дописываем его not-маску
            const parentView = findParentView(viewHost);
            if (!parentView)
                return [];
            const limit = ViewWidgetHelper.getUiNotMask(parentView); // уже строка из :not(...)
            const eff = withNotCss(mask, limit);
            return qAll(parentView, eff);
        }
        //  'view' и любые нестандартные значения обрабатываем как 'view'
        default: {
            const limit = uiNotMask ?? ViewWidgetHelper.getUiNotMask(viewHost);
            const eff = withNotCss(mask, limit);
            return qAll(viewHost, eff);
        }
    }
}
/** Поиск первого элемента по маске*/
function findFirstBySearchMask(sourceElement, sm) {
    const view = ViewWidgetHelper.closestOnByName(sourceElement);
    if (!view)
        return undefined;
    const items = findBySearchMask(view.element, sm, sourceElement, view.getUiNotMask());
    if (items.length > 0)
        return items[0];
    else
        return undefined;
}
/* ================= helpers ================= */
/** querySelectorAll → Array<HTMLElement> */
function qAll(scope, selector) {
    // Исправляем маску GUID в селекторе: [id=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx]
    selector = fixGuidSelectors(selector);
    if (scope instanceof Element || scope instanceof DocumentFragment) {
        return Array.from(scope.querySelectorAll(selector));
    }
    return Array.from(scope.querySelectorAll(selector));
}
/**
 * Добавляет кавычки вокруг GUID внутри [id=...] и экранирует.
 * Пример:
 *   [id=30140a01-20fa-4c98-8129-9de9d96995e8]
 * → [id="30140a01-20fa-4c98-8129-9de9d96995e8"]
 */
function fixGuidSelectors(sel) {
    // GUID шаблон: 8-4-4-4-12 шестнадцатеричных символов
    const re = /\[([a-zA-Z0-9_-]+)=([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\]/g;
    return sel.replace(re, (_m, attr, guid) => `[${attr}="${guid}"]`);
}
/**
 * uiNotMask может быть:
 *  - готовой цепочкой :not(...):not(...), тогда добавляется как есть
 *  - списком селекторов через запятую, тогда каждый превращается в :not(<sel> *)
 */
function withNotCss(mask, uiNotMask) {
    if (!uiNotMask)
        return mask;
    const raw = uiNotMask.trim();
    // Готовая цепочка :not(…) → возвращаем как есть
    if (raw.includes(':not('))
        return `:is(${mask})${raw}`;
    // Иначе это список селекторов → собираем цепочку :not(...)
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length === 0)
        return mask;
    const nots = parts.map(sel => {
        return `:not(${sel} *)`;
    }).join('');
    return `:is(${mask})${nots}`;
}
/** Родительский view для текущего viewHost */
function findParentView(viewHost) {
    return (viewHost.parentElement?.closest(VIEW_SELECTOR) ?? null);
}

export { findBySearchMask, findFirstBySearchMask, hasOwnSearchArea, parseSearchMask };
